import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { commentService, reactionService, postService } from '../../services/api';
import PhotoLightbox from '../PhotoLightbox/PhotoLightbox';
import {
  FiMessageCircle, FiShare2, FiMoreHorizontal,
  FiMapPin, FiEdit2, FiTrash2, FiBookmark
} from 'react-icons/fi';
import './Post.css';


const Post = ({ post, onUpdate, onDelete }) => {
  const { user } = useAuth();
  const reactionTimeoutRef = useRef(null);
  const [showReactions, setShowReactions] = useState(false);
  const [reactionTypes, setReactionTypes] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [currentReaction, setCurrentReaction] = useState(post.currentUserReaction);
  const [reactionSummary, setReactionSummary] = useState(post.reactionSummary || []);
  const [likeCount, setLikeCount] = useState(post.likeCount);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [showReactionsTooltip, setShowReactionsTooltip] = useState(false);
  const [reactionsDetail, setReactionsDetail] = useState(null);
  const [loadingReactionsDetail, setLoadingReactionsDetail] = useState(false);
  const reactionsTooltipTimeout = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content || '');
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    const fetchReactionTypes = async () => {
      try {
        const response = await reactionService.getReactionTypes();
        if (response.success) {
          setReactionTypes(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch reaction types:', err);
      }
    };
    fetchReactionTypes();
  }, []);

  // Lightbox state
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const isOwner = user?.memberID === post.memberID;
  const canManagePost = isOwner || user?.isAdmin;

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Lightbox functions
  const openLightbox = (photo, index) => {
    setLightboxPhoto(photo);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxPhoto(null);
  };

  const navigateLightbox = (direction) => {
    const photos = post.photos || [];
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < photos.length) {
      setLightboxIndex(newIndex);
      setLightboxPhoto(photos[newIndex]);
    }
  };

  const handleReaction = async (reactionTypeId) => {
    try {
      if (currentReaction?.reactionTypeID === reactionTypeId) {
        // Remove reaction
        await reactionService.removePostReaction(post.postID);
        setCurrentReaction(null);
        setLikeCount(prev => prev - 1);
        setReactionSummary(prev => {
          const updated = prev.map(r =>
            r.reactionTypeID === reactionTypeId
              ? { ...r, count: r.count - 1 }
              : r
          ).filter(r => r.count > 0);
          return updated;
        });
      } else {
        // Add/update reaction
        const response = await reactionService.addReaction({
          reactionTypeID: reactionTypeId,
          postID: post.postID
        });

        if (response.success) {
          const wasNewReaction = !currentReaction;
          setCurrentReaction(response.data);

          if (wasNewReaction) {
            setLikeCount(prev => prev + 1);
          }

          setReactionSummary(prev => {
            let updated = [...prev];

            // Remove old reaction count if changing
            if (currentReaction) {
              updated = updated.map(r =>
                r.reactionTypeID === currentReaction.reactionTypeID
                  ? { ...r, count: r.count - 1 }
                  : r
              ).filter(r => r.count > 0);
            }

            // Add new reaction count
            const existingIdx = updated.findIndex(r => r.reactionTypeID === reactionTypeId);
            if (existingIdx >= 0) {
              updated[existingIdx] = { ...updated[existingIdx], count: updated[existingIdx].count + 1 };
            } else {
              const reaction = reactionTypes.find(r => r.reactionTypeID === reactionTypeId);
              updated.push({
                reactionTypeID: reactionTypeId,
                reactionName: reaction?.reactionName,
                emojiCode: reaction?.emojiCode,
                count: 1
              });
            }

            return updated.sort((a, b) => b.count - a.count);
          });
        }
      }
    } catch (err) {
      console.error('Failed to react:', err);
    }
    setShowReactions(false);
  };

  const loadComments = async () => {
    if (loadingComments) return;

    setLoadingComments(true);
    try {
      const response = await postService.getPostComments(post.postID);
      if (response.success) {
        setComments(response.data);
      }
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
    setLoadingComments(false);
  };

  const handleToggleComments = () => {
    if (!showComments && comments.length === 0) {
      loadComments();
    }
    setShowComments(!showComments);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const response = await commentService.createComment({
        content: newComment,
        postID: post.postID
      });

      if (response.success) {
        setComments(prev => [...prev, response.data]);
        setNewComment('');
        if (onUpdate) {
          onUpdate({ ...post, commentCount: post.commentCount + 1 });
        }
      }
    } catch (err) {
      console.error('Failed to add comment:', err);
    }
  };

  const handleEditPost = async () => {
    if (!editContent.trim()) return;
    try {
      setEditLoading(true);
      const response = await postService.updatePost(post.postID, { content: editContent });
      if (response.success) {
        setIsEditing(false);
        if (onUpdate) onUpdate({ ...post, content: editContent, isEdited: true });
      }
    } catch (err) {
      console.error('Failed to edit post:', err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editCommentContent.trim()) return;
    try {
      const response = await commentService.updateComment(commentId, editCommentContent);
      if (response.success) {
        setComments(prev => prev.map(c =>
          c.commentID === commentId ? { ...c, content: editCommentContent } : c
        ));
        setEditingCommentId(null);
        setEditCommentContent('');
      }
    } catch (err) {
      console.error('Failed to edit comment:', err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      const response = await commentService.deleteComment(commentId);
      if (response.success) {
        setComments(prev => prev.filter(c => c.commentID !== commentId));
      }
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      if (onDelete) {
        onDelete(post.postID);
      }
    }
    setShowMenu(false);
  };

  const handleShowReactionsDetail = async () => {
    setShowReactionsTooltip(true);
    try {
      setLoadingReactionsDetail(true);
      const response = await postService.getPostReactions(post.postID);
      if (response.success) {
        setReactionsDetail(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch reactions detail:', err);
    } finally {
      setLoadingReactionsDetail(false);
    }
  };

  return (
    <article className="post card">
      {/* Post Header */}
      <div className="post-header">
        <Link to={`/member/${post.memberID}`} className="post-author">
          {post.member?.profilePhotoURL ? (
            <img
              src={post.member.profilePhotoURL}
              alt={post.member.fullName}
              className="avatar"
            />
          ) : (
            <div className="avatar">
              {getInitials(post.member?.firstName, post.member?.lastName)}
            </div>
          )}
          <div className="post-author-info">
            <span className="post-author-name">{post.member?.fullName}</span>
            <span className="post-meta">
              {formatDistanceToNow(new Date(post.createdDate + 'Z'), { addSuffix: true })}
              {post.isEdited && ' · Edited'}
              {post.locationName && (
                <>
                  <FiMapPin className="post-location-icon" />
                  {post.locationName}
                </>
              )}
            </span>
          </div>
        </Link>

        {canManagePost && (
          <div className="post-menu-container">
            <button
              className="post-menu-btn"
              onClick={() => setShowMenu(!showMenu)}
            >
              <FiMoreHorizontal />
            </button>

            {showMenu && (
              <>
                <div className="menu-backdrop" onClick={() => setShowMenu(false)} />
                <div className="post-menu">
                  <button className="post-menu-item" onClick={() => { setIsEditing(true); setShowMenu(false); }}>
                    <FiEdit2 /> Edit Post
                  </button>
                  <button className="post-menu-item">
                    <FiBookmark /> {post.isPinned ? 'Unpin' : 'Pin'} Post
                  </button>
                  <button className="post-menu-item delete" onClick={handleDelete}>
                    <FiTrash2 /> Delete Post
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Post Content */}
      <div className="post-content">
        {isEditing ? (
          <div className="post-edit-form">
            <textarea
              className="post-edit-textarea"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={4}
              autoFocus
            />
            <div className="post-edit-actions">
              <button className="btn btn-ghost btn-sm" onClick={() => setIsEditing(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleEditPost} disabled={editLoading}>
                {editLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          post.content && (
            <p className="post-text">{post.content}</p>
          )
        )}

        {/* Life Event Badge */}
        {post.lifeEventType && (
          <div className="post-life-event">
            🎉 {post.lifeEventType}
          </div>
        )}

        {/* Photos - now clickable */}
        {post.photos && post.photos.length > 0 && (
          <div className={`post-photos photos-${Math.min(post.photos.length, 4)}`}>
            {post.photos.slice(0, 4).map((photo, idx) => (
              <div
                key={photo.photoID}
                className="post-photo-item"
                onClick={() => openLightbox(photo, idx)}
              >
                <img src={photo.thumbnailURL || photo.fileURL} alt="" />
                {idx === 3 && post.photos.length > 4 && (
                  <div className="post-photo-more">+{post.photos.length - 4}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Tagged Members */}
        {post.taggedMembers && post.taggedMembers.length > 0 && (
          <div className="post-tags">
            with {post.taggedMembers.map((m, idx) => (
              <span key={m.memberID}>
                <Link to={`/member/${m.memberID}`}>{m.fullName}</Link>
                {idx < post.taggedMembers.length - 1 && ', '}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Reactions Summary */}
      {(likeCount > 0 || post.commentCount > 0) && (
        <div className="post-stats">
          {likeCount > 0 && (
            <div
              className="post-reactions-summary"
              onMouseEnter={() => {
                clearTimeout(reactionsTooltipTimeout.current);
                handleShowReactionsDetail();
              }}
              onMouseLeave={() => {
                reactionsTooltipTimeout.current = setTimeout(() => setShowReactionsTooltip(false), 300);
              }}
              onClick={handleShowReactionsDetail}
            >
              <span className="reaction-emojis">
                {reactionSummary.slice(0, 3).map(r => r.emojiCode).join('')}
              </span>
              <span>{likeCount}</span>

              {showReactionsTooltip && (
                <div
                  className="reactions-tooltip"
                  onMouseEnter={() => clearTimeout(reactionsTooltipTimeout.current)}
                  onMouseLeave={() => {
                    reactionsTooltipTimeout.current = setTimeout(() => setShowReactionsTooltip(false), 300);
                  }}
                >
                  {loadingReactionsDetail ? (
                    <span className="reactions-tooltip-loading">Loading...</span>
                  ) : reactionsDetail?.reactions?.length > 0 ? (
                    reactionsDetail.reactions.map((r, i) => (
                      <div key={i} className="reactions-tooltip-row">
                        <span className="reactions-tooltip-emoji">{r.reactionType?.emojiCode}</span>
                        <span className="reactions-tooltip-name">{r.member?.fullName}</span>
                      </div>
                    ))
                  ) : (
                    <span className="reactions-tooltip-loading">No reactions</span>
                  )}
                </div>
              )}
            </div>
          )}
          {post.commentCount > 0 && (
            <button className="post-comments-count" onClick={handleToggleComments}>
              {post.commentCount} comment{post.commentCount !== 1 ? 's' : ''}
            </button>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="post-actions">
        <div className="reaction-container">
          <button
            className={`post-action-btn ${currentReaction ? 'reacted' : ''}`}
            onClick={() => currentReaction ? handleReaction(currentReaction.reactionTypeID) : setShowReactions(!showReactions)}
            onMouseEnter={() => {
              clearTimeout(reactionTimeoutRef.current);
              setShowReactions(true);
            }}
            onMouseLeave={() => {
              reactionTimeoutRef.current = setTimeout(() => setShowReactions(false), 800);
            }}
          >
            <span className="reaction-emoji">
              {currentReaction ?
                reactionTypes.find(r => r.reactionTypeID === currentReaction.reactionTypeID)?.emojiCode || '👍'
                : '👍'}
            </span>
            <span>{currentReaction?.reactionType?.reactionName || 'React'}</span>
          </button>

          {showReactions && (
            <div
              className="reactions-picker"
              onMouseEnter={() => {
                clearTimeout(reactionTimeoutRef.current);
                setShowReactions(true);
              }}
              onMouseLeave={() => {
                reactionTimeoutRef.current = setTimeout(() => setShowReactions(false), 400);
              }}
            >
              {reactionTypes.map(reaction => (
                <button
                  key={reaction.reactionTypeID}
                  className="reaction-btn"
                  onClick={() => handleReaction(reaction.reactionTypeID)}
                  title={reaction.reactionName}
                >
                  {reaction.emojiCode}
                </button>
              ))}
            </div>
          )}
        </div>

        <button className="post-action-btn" onClick={handleToggleComments}>
          <FiMessageCircle />
          <span>Comment</span>
        </button>

        <button className="post-action-btn">
          <FiShare2 />
          <span>Share</span>
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="post-comments">
          {loadingComments ? (
            <div className="comments-loading">
              <span className="spinner spinner-sm"></span>
              Loading comments...
            </div>
          ) : (
            <>
              {comments.map(comment => (
                <div key={comment.commentID} className="comment">
                  <Link to={`/member/${comment.memberID}`}>
                    {comment.member?.profilePhotoURL ? (
                      <img src={comment.member.profilePhotoURL} alt="" className="avatar avatar-sm" />
                    ) : (
                      <div className="avatar avatar-sm">
                        {getInitials(comment.member?.firstName, comment.member?.lastName)}
                      </div>
                    )}
                  </Link>
                  <div className="comment-body">
                    <div className="comment-bubble">
                      <Link to={`/member/${comment.memberID}`} className="comment-author">
                        {comment.member?.fullName}
                      </Link>
                      {editingCommentId === comment.commentID ? (
                        <div className="comment-edit-form">
                          <textarea
                            className="comment-edit-textarea"
                            value={editCommentContent}
                            onChange={(e) => setEditCommentContent(e.target.value)}
                            rows={4}
                            autoFocus
                          />
                          <div className="comment-edit-actions">
                            <button className="btn btn-ghost btn-sm" onClick={() => setEditingCommentId(null)}>Cancel</button>
                            <button className="btn btn-primary btn-sm" onClick={() => handleEditComment(comment.commentID)}>Save</button>
                          </div>
                        </div>
                      ) : (
                        <p>{comment.content}</p>
                      )}
                    </div>
                    <div className="comment-meta">
                      <span>{formatDistanceToNow(new Date(comment.createdDate + 'Z'), { addSuffix: true })}</span>
                      {editingCommentId !== comment.commentID && (
                        <>
                          {(comment.memberID === user?.memberID || user?.isAdmin) && (
                            <button className="comment-action" onClick={() => { setEditingCommentId(comment.commentID); setEditCommentContent(comment.content); }}>Edit</button>
                          )}
                          {(comment.memberID === user?.memberID || user?.isAdmin) && (
                            <button className="comment-action comment-action-delete" onClick={() => handleDeleteComment(comment.commentID)}>Delete</button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              <form className="comment-form" onSubmit={handleAddComment}>
                {user?.profilePhotoURL ? (
                  <img src={user.profilePhotoURL} alt="" className="avatar avatar-sm" />
                ) : (
                  <div className="avatar avatar-sm">
                    {getInitials(user?.firstName, user?.lastName)}
                  </div>
                )}
                <input
                  type="text"
                  className="comment-input"
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
              </form>
            </>
          )}
        </div>
      )}

      {/* Photo Lightbox */}
      {lightboxPhoto && (
        <PhotoLightbox
          photo={lightboxPhoto}
          onClose={closeLightbox}
          onPrev={() => navigateLightbox(-1)}
          onNext={() => navigateLightbox(1)}
          hasPrev={lightboxIndex > 0}
          hasNext={lightboxIndex < (post.photos?.length || 0) - 1}
          onDelete={null}
          onCaptionUpdated={(photoId, newCaption) => {
            setLightboxPhoto(prev => ({ ...prev, caption: newCaption }));
          }}
        />
      )}
    </article>
  );
};

export default Post;